// Kartenablauf C1 (Entscheidung 21, docs/PLAN_VARIANTE_C_V1.md): Regeln und Lernstand als reine Funktionen, ohne Oberfläche, Speicher und Uhr.
// Eingaben sind die v2-Ereignisse (learning_events_v2), der Inhalt und die Policy; `now` wird immer übergeben, nie abgefragt.
//
// Zwei Fähigkeiten je Lernziel mit eigenem FSRS-Plan: h = Hören (Ton zuerst), p = Selbst sagen. Die Policy entscheidet nur, welche Aufgabe
// angeboten wird; der Planer (ts-fsrs, Einstellungen wie bisher) verarbeitet ausschließlich beobachtete Abrufe. Kein Erfolg einer Richtung
// wird als Erfolg der anderen gespeichert, keine Bewertung wird erfunden.
//
// Hörmodus je Lernziel (nur Häufigkeit der Hörangebote; p läuft immer nach eigenem Plan):
//   build (Aufbau) → control (Kontrollphase) → refresh (Nachfestigen) → control
// Policy c1 schaltet um, Vergleichsvariante a1 bleibt immer im Aufbau.
import { toStored, fromStored } from './learning.js';
import { CHECKS_PER_DAY } from './placement.js';

const H = 3600e3, D = 24 * H;

const BASE = {
  initialH: 24 * H,          // erste Hörfrage frühestens 24 h nach dem Kennenlernen
  initialP: 48 * H,          // erste Produktion frühestens 48 h danach
  independent: 24 * H,       // unabhängiger Nachweis: mindestens 24 h nach der letzten gezielten Beschäftigung
  buildCount: 3, buildSpan: 14 * D, buildGroups: 2, pMaxAge: 30 * D,
  refreshCount: 2, refreshGap: 48 * H, refreshGroups: 2,
  controlMinGap: 14 * D,     // Kontrolle frühestens 14 Tage nach Eintritt bzw. letztem Hörabruf
  controlsPerDay: 2, controlEvery: 5, voluntaryPerDay: 1,
  retryPer24h: 1,            // nach Note 1 höchstens ein zusätzlicher Versuch je Ziel und Richtung in 24 h
  offerGap: 24 * H,          // höchstens ein regulärer gezielter Abruf je Ziel in 24 h
  standardRate: 1,           // Standardtempo für Hörfragen
  maxPlaysQualified: 2,
  placementPerDay: CHECKS_PER_DAY,   // Hörprüfungen vorausgesetzter Wörter je Lerntag (Einstufung, Entscheidung 34)
};
export const POLICIES = {
  c1: Object.freeze({ ...BASE, version: 'c1', reduce: true }),
  a1: Object.freeze({ ...BASE, version: 'a1', reduce: false }),
};
export const SCHEDULER_VERSION = 'ts-fsrs-5.4.1/ultreya';
export const policyOf = v => POLICIES[v] || POLICIES.c1;

// ---------- Notenbegrenzung beim Hören (Plan Abschnitt 5) ----------
// a = Zusammenfassung eines Versuchs: { assisted, plays, minRate }
export function hearingCap(a, P = POLICIES.c1) {
  if (a.assisted) return 1;
  if ((a.plays || 0) >= 3 || (a.minRate ?? P.standardRate) < P.standardRate) return 2;
  if ((a.plays || 0) === 2) return 3;
  return 4;
}
export const normalizeRating = (skill, rating, a, P) => skill === 'h' ? Math.min(rating, hearingCap(a, P)) : rating;

// ---------- Versuche aus Ereignissen ----------
// Ereignisarten: presented, attempt_progress (milestone), introduced, assistance, review, skipped, exposure
const ENGAGING_MILESTONES = new Set(['audio_start', 'reveal']);
export function attemptsOf(events) {
  const byId = new Map();
  const seen = new Set();
  for (const e of events) {
    if (seen.has(e.id)) continue; seen.add(e.id);
    if (!e.attempt_id) continue;
    let a = byId.get(e.attempt_id);
    if (!a) {
      a = { id: e.attempt_id, target: e.target_id, rev: e.target_revision, sentence: e.sentence_id, ctx: e.context_group_id, skill: e.skill || null, role: e.task_role,
            presentedAt: null, engagedAt: null, introducedAt: null, review: null, skipped: null, assisted: false, plays: 0, completed: false, minRate: null,
            audioStarted: false, revealedAt: null, events: [], controlKind: null };
      byId.set(e.attempt_id, a);
    }
    a.events.push(e);
    if (e.skill && !a.skill) a.skill = e.skill;
    if (e.meta && e.meta.control_kind) a.controlKind = e.meta.control_kind;
    const t = e.occurred_at;
    const engage = () => { if (a.engagedAt === null || t < a.engagedAt) a.engagedAt = t; };
    switch (e.event_kind) {
      case 'presented':
        if (a.presentedAt === null || t < a.presentedAt) a.presentedAt = t;
        if (a.skill === 'p' || a.role === 'introduction') engage();   // P verrät mit der Aufgabe schon gezielten Inhalt; die Einführung zeigt ihn
        break;
      case 'attempt_progress': {
        const m = e.milestone;
        if (m === 'audio_start') { a.audioStarted = true; a.plays = Math.max(a.plays, 1); }
        else if (m === 'audio_complete') a.completed = true;
        else if (m === 'play_2') a.plays = Math.max(a.plays, 2);
        else if (m === 'play_3') a.plays = Math.max(a.plays, 3);
        else if (m === 'slowed') a.minRate = Math.min(a.minRate ?? 1, e.meta && e.meta.rate ? e.meta.rate : 0.75);
        else if (m === 'reveal') { if (a.revealedAt === null || t < a.revealedAt) a.revealedAt = t; }
        if (ENGAGING_MILESTONES.has(m)) engage();
        break;
      }
      case 'assistance': a.assisted = true; engage(); break;
      case 'introduced': if (a.introducedAt === null || t < a.introducedAt) a.introducedAt = t; engage(); break;
      case 'review':
        if (!a.review || t < a.review.at || (t === a.review.at && e.id < a.review.id)) a.review = { at: t, rating: e.rating, outcome: e.answer_outcome, id: e.id };
        // Zusammenfassung im Abschluss: nur verschärfend übernehmen (Hilfe, mehr Wiedergaben, langsamer); ein späterer Abschluss macht Hilfe nie ungeschehen
        if (e.text_help_before_reveal || e.meaning_help_before_reveal) a.assisted = true;
        if (e.play_count_before_reveal) a.plays = Math.max(a.plays, e.play_count_before_reveal);
        if (e.min_playback_rate_before_reveal) a.minRate = Math.min(a.minRate ?? 1, e.min_playback_rate_before_reveal);
        if (e.audio_completed) a.completed = true;
        if (e.audio_started) a.audioStarted = true;
        engage();
        break;
      case 'skipped': if (!a.skipped || t < a.skipped.at) a.skipped = { at: t, reason: e.skip_reason || '' }; break;
      default: break;
    }
  }
  return [...byId.values()];
}

// ---------- Lernstand C1 ----------
// content: App-Inhalt (units mit c1); sch: Planer ({ next, empty }); P: Policy. Ergebnis: { targets: { id: T }, attempts, controlsByDay, … }
export function deriveC1(events, content, sch, P = POLICIES.c1) {
  const units = Object.fromEntries(content.units.map(u => [u.id, u]));
  const attempts = attemptsOf(events);
  const targets = {};
  const T = id => targets[id] || (targets[id] = {
    id, rev: (units[id] && units[id].c1 && units[id].c1.rev) || 1, introducedAt: null,
    h: { card: null, reviews: [] }, p: { card: null, reviews: [] },
    mode: 'build', modeSince: null, series: [], everControl: false, lastQualifiedP: null,
    lastEngageAt: null, lastHProblemAt: null, lastHAttemptAt: null, controlBase: null, lastControlCtx: null, lastSuccessCtx: null,
    openAttempt: null, reasons: [],
  });
  const sentenceEngage = {};   // letzte gezielte Beschäftigung je Satz (auch über andere Zielzuordnungen: Vorentlastung)

  // Vorgänge in zeitlicher Reihenfolge: Einführung, Beschäftigung, Abschluss (Bewertung), abgebrochene Hilfe
  const facts = [];
  for (const a of attempts) {
    if (a.introducedAt !== null) facts.push({ at: a.introducedAt, kind: 'introduced', a, ord: 1 });
    if (a.engagedAt !== null) facts.push({ at: a.engagedAt, kind: 'engage', a, ord: 0 });
    if (a.review) facts.push({ at: a.review.at, kind: 'review', a, ord: 2 });
    else if (a.assisted && !a.introducedAt && a.skill === 'h') facts.push({ at: lastHelpAt(a), kind: 'help-abort', a, ord: 2 });
  }
  facts.sort((x, y) => (x.at - y.at) || (x.ord - y.ord) || (x.a.id < y.a.id ? -1 : x.a.id > y.a.id ? 1 : 0));

  // Beschäftigungen vor einem Versuch, ohne den Versuch selbst
  const engageLog = {};   // target -> [{ at, attempt }]
  const prevEngage = (log, before, attemptId) => { let m = null; for (const x of log || []) if (x.attempt !== attemptId && x.at < before && (m === null || x.at > m)) m = x.at; return m; };

  for (const f of facts) {
    const a = f.a, t = T(a.target);
    // Neue Zielrevision (neue Bedeutung oder Konstruktion) = neues Ziel: Einführung, Bewertungen und Nachweise der alten Revision zählen nicht.
    // Beschäftigung zählt weiter (für die 24-h-Abstände ist egal, welche Fassung man gerade gesehen hat).
    if (f.kind !== 'engage' && (a.rev ?? 1) !== t.rev) continue;
    if (f.kind === 'engage') {
      (engageLog[a.target] || (engageLog[a.target] = [])).push({ at: f.at, attempt: a.id });
      (sentenceEngage[a.sentence] || (sentenceEngage[a.sentence] = [])).push({ at: f.at, attempt: a.id });
      if (t.lastEngageAt === null || f.at > t.lastEngageAt) t.lastEngageAt = f.at;
      continue;
    }
    if (f.kind === 'introduced') { if (t.introducedAt === null) { t.introducedAt = f.at; t.modeSince = f.at; } continue; }
    const start = a.engagedAt ?? a.presentedAt ?? f.at;
    const pe = prevEngage(engageLog[a.target], start, a.id), ps = prevEngage(sentenceEngage[a.sentence], start, a.id);
    const independent = (pe === null || start - pe >= P.independent) && (ps === null || start - ps >= P.independent);
    const revOk = a.rev === t.rev;
    if (f.kind === 'help-abort') {   // Hilfe gebraucht, Versuch ohne Abschluss: Signal ohne erfundene Note
      hProblem(t, f.at, P);
      continue;
    }
    const sk = a.skill === 'p' ? 'p' : 'h', S = t[sk];
    const r = normalizeRating(sk, a.review.rating, a, P);
    const card = S.card ? fromStored(S.card) : sch.empty(new Date(f.at));
    S.card = toStored(sch.next(card, new Date(f.at), r));
    S.reviews.push({ at: f.at, r, role: a.role, ctx: a.ctx, attempt: a.id, sentence: a.sentence });
    if (sk === 'p') {
      if (r >= 3 && !a.assisted && independent && revOk && t.introducedAt !== null && f.at > t.introducedAt) t.lastQualifiedP = f.at;
      continue;
    }
    t.lastHAttemptAt = f.at;
    const problem = r <= 2 || a.assisted;
    const qualified = !problem && r >= 3 && a.completed && (a.plays || 0) <= P.maxPlaysQualified && (a.minRate ?? 1) >= P.standardRate && independent && revOk;
    if (problem) { hProblem(t, f.at, P); continue; }
    t.lastSuccessCtx = a.ctx;
    if (a.role === 'control') t.lastControlCtx = a.ctx;
    if (!qualified) continue;
    const gap = t.mode === 'refresh' ? P.refreshGap : P.independent;
    const last = t.series[t.series.length - 1];
    if (!last || f.at - last.at >= gap) t.series.push({ at: f.at, ctx: a.ctx });
    if (!P.reduce || t.mode === 'control') continue;
    const groups = new Set(t.series.map(x => x.ctx)).size;
    if (t.mode === 'build') {
      const span = t.series.length ? t.series[t.series.length - 1].at - t.series[0].at : 0;
      const pOk = t.lastQualifiedP !== null && f.at - t.lastQualifiedP <= P.pMaxAge;
      if (t.series.length >= P.buildCount && span >= P.buildSpan && groups >= P.buildGroups && pOk) toControl(t, f.at);
    } else if (t.mode === 'refresh') {
      if (t.series.length >= P.refreshCount && groups >= P.refreshGroups) toControl(t, f.at);
    }
  }
  // Offene Versuche (begonnen, ohne Abschluss): können fortgesetzt werden
  for (const a of attempts) {
    if (a.review || a.introducedAt !== null || a.skipped) continue;
    if (targets[a.target] && (a.rev ?? 1) !== targets[a.target].rev) continue;
    const t = targets[a.target] ? targets[a.target] : (a.role === 'introduction' ? T(a.target) : null);
    if (!t) continue;
    if (!t.openAttempt || (a.presentedAt ?? 0) > (t.openAttempt.presentedAt ?? 0)) t.openAttempt = a;
  }
  for (const t of Object.values(targets)) t.reasons = reductionReasons(t, P, units[t.id]);
  const sentenceLast = {};   // letzte gezielte Beschäftigung je Satz (Vorentlastung, für die Satzwahl)
  for (const a of attempts) if (a.engagedAt !== null && (sentenceLast[a.sentence] === undefined || a.engagedAt > sentenceLast[a.sentence])) sentenceLast[a.sentence] = a.engagedAt;
  return { targets, attempts, policy: P, sentenceLast };
}

function lastHelpAt(a) { let m = null; for (const e of a.events) if (e.event_kind === 'assistance' && (m === null || e.occurred_at > m)) m = e.occurred_at; return m ?? a.presentedAt ?? 0; }
function hProblem(t, at, P) {
  t.lastHProblemAt = at; t.series = [];
  if (t.mode === 'control') { t.mode = 'refresh'; t.modeSince = at; }
}
function toControl(t, at) { t.mode = 'control'; t.modeSince = at; t.controlBase = at; t.everControl = true; t.series = []; }

// Warum (noch) keine Kontrollphase? Für Redaktion und Abnahmebericht.
export function reductionReasons(t, P, unit) {
  if (!P.reduce) return ['Vergleichsvariante a1: keine Reduktion'];
  if (t.mode === 'control') return [];
  const out = [];
  const tasks = (unit && unit.c1 && unit.c1.tasks) || [];
  if (new Set(tasks.filter(x => x.h).map(x => x.ctx)).size < 2) out.push('zweiter Kontext fehlt');
  if (t.mode === 'build') {
    if (t.series.length < P.buildCount) out.push(`${t.series.length} von ${P.buildCount} unabhängigen Hörerfolgen`);
    else if (t.series[t.series.length - 1].at - t.series[0].at < P.buildSpan) out.push('Zeitspanne unter 14 Tagen');
    if (new Set(t.series.map(x => x.ctx)).size < P.buildGroups && t.series.length) out.push('Hörerfolge nur in einer Kontextgruppe');
    if (t.lastQualifiedP === null) out.push('kein verzögerter Sprech-Erfolg');
  } else if (t.mode === 'refresh') out.push(`Nachfestigen: ${t.series.length} von ${P.refreshCount} Hörerfolgen`);
  return out;
}

// ---------- Vorschau: derselbe Planer, dieselbe Normalisierung wie beim Speichern ----------
export function previewDue(T, skill, rating, attemptSummary, at, sch, P = POLICIES.c1) {
  const S = T ? T[skill] : null;
  const card = S && S.card ? fromStored(S.card) : sch.empty(new Date(at));
  const r = normalizeRating(skill, rating, attemptSummary, P);
  return { r, due: sch.next(card, new Date(at), r).due.getTime() };
}

// ---------- Lerntag ----------
const dayFormatters = new Map(), dayCache = new Map();
export function studyDay(t, tz) {
  const key = `${tz || ''}|${t}`;
  if (dayCache.has(key)) return dayCache.get(key);
  let day;
  try {
    if (!dayFormatters.has(tz)) dayFormatters.set(tz, new Intl.DateTimeFormat('sv-SE', {timeZone:tz}));
    day = dayFormatters.get(tz).format(new Date(t));
  } catch { day = new Date(t).toISOString().slice(0, 10); }
  if (dayCache.size >= 32000) dayCache.clear();
  dayCache.set(key,day);return day;
}
export const localTimezone = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } };
